import { z } from 'zod';
import type { Schemas } from '../../api/client';
import type { Contact } from '../../components/pickers';
import type { Quotation } from './model';
import type { DiscountInput, DiscountType, TotalsLine } from './totals';

/** One editable quotation line. Item details are snapshots so existing lines render before lookups. */
export interface QuoteLineDraft {
  /** Stable React key. */
  key: string;
  variantId: string;
  /** Null for lines loaded from a saved quotation until the item is looked up. */
  productId: string | null;
  sku: string;
  description: string;
  unitId: string;
  unitSymbol: string;
  factor: string;
  /** Unknown (undefined) until the item is looked up. */
  baseAllowsFraction?: boolean;
  /** Sale price per base unit, when known (for the list-price hint). */
  salePrice: string | null;
  qty: string;
  unitPrice: string;
  discountType: DiscountType;
  discountValue: string;
  /** '' = no tax. */
  taxRateId: string;
}

export function emptyQuoteLine(): QuoteLineDraft {
  return {
    key: crypto.randomUUID(),
    variantId: '',
    productId: null,
    sku: '',
    description: '',
    unitId: '',
    unitSymbol: '',
    factor: '1',
    salePrice: null,
    qty: '1',
    unitPrice: '',
    discountType: 'PERCENT',
    discountValue: '',
    taxRateId: '',
  };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function quoteFormSchema(today: string) {
  return z
    .object({
      contact: z.custom<Contact | null>().refine((c): boolean => c !== null, 'Pick a customer'),
      docDate: z
        .string()
        .regex(DATE_RE, 'Pick a date')
        .refine((d) => d <= today, 'A quotation cannot be dated in the future'),
      validUntil: z.string().refine((d) => d === '' || DATE_RE.test(d), 'Pick a date'),
      reference: z.string().trim().max(100, 'At most 100 characters'),
      notes: z.string().trim().max(2000, 'At most 2000 characters'),
      terms: z.string().trim().max(4000, 'At most 4000 characters'),
      discountType: z.enum(['AMOUNT', 'PERCENT']),
      discountValue: z.string().trim(),
      lines: z
        .array(
          z.custom<QuoteLineDraft>().superRefine((l, ctx) => {
            if (!l.variantId)
              ctx.addIssue({ code: 'custom', path: ['description'], message: 'Pick an item' });
            else if (!l.description.trim())
              ctx.addIssue({
                code: 'custom',
                path: ['description'],
                message: 'Enter a description',
              });
            else if (l.description.trim().length > 400)
              ctx.addIssue({
                code: 'custom',
                path: ['description'],
                message: 'At most 400 characters',
              });
          }),
        )
        .min(1, 'Add at least one item'),
    })
    .superRefine((v, ctx) => {
      if (v.validUntil && v.validUntil < v.docDate)
        ctx.addIssue({
          code: 'custom',
          path: ['validUntil'],
          message: 'Must be on or after the quotation date',
        });
      const seen = new Map<string, number>();
      v.lines.forEach((l, i) => {
        const key = `${l.variantId}:${l.unitId}`;
        const prev = seen.get(key);
        if (prev !== undefined)
          ctx.addIssue({
            code: 'custom',
            path: ['lines', i, 'description'],
            message: `Same item and unit as line ${String(prev + 1)} — change the quantity there`,
          });
        else seen.set(key, i);
      });
    });
}

export type QuoteFormValues = z.input<ReturnType<typeof quoteFormSchema>>;

function discountOf(type: DiscountType, value: string): DiscountInput | null {
  const v = value.trim();
  return v === '' || Number(v) === 0 ? null : { type, value: v };
}

/** Lines in the shape the totals engine takes. */
export function toTotalsLines(
  lines: QuoteLineDraft[],
  rateOf: (taxRateId: string) => string | undefined,
): TotalsLine[] {
  return lines.map((l) => ({
    qty: l.qty,
    factor: l.factor,
    baseAllowsFraction: l.baseAllowsFraction,
    unitPrice: l.unitPrice,
    discount: discountOf(l.discountType, l.discountValue),
    taxRate: l.taxRateId ? (rateOf(l.taxRateId) ?? '0') : null,
  }));
}

export function documentDiscountOf(
  values: Pick<QuoteFormValues, 'discountType' | 'discountValue'>,
) {
  return discountOf(values.discountType, values.discountValue);
}

type Body = Schemas['CreateQuotationRequest'];

function toBody(values: QuoteFormValues): Omit<Body, 'contactId'> & { contactId: string } {
  const text = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    contactId: values.contact?.id ?? '',
    docDate: values.docDate,
    validUntil: values.validUntil === '' ? null : values.validUntil,
    discount: documentDiscountOf(values),
    reference: text(values.reference),
    notes: text(values.notes),
    terms: text(values.terms),
    lines: values.lines.map((l) => ({
      variantId: l.variantId,
      unitId: l.unitId,
      qty: l.qty.trim(),
      unitPrice: l.unitPrice.trim(),
      discount: discountOf(l.discountType, l.discountValue),
      taxRateId: l.taxRateId || null,
      description: l.description.trim(),
    })),
  };
}

/** Payload for POST /quotations (a client id makes a retried save idempotent). */
export function toCreateRequest(values: QuoteFormValues, id?: string): Body {
  return { ...(id ? { id } : {}), ...toBody(values) };
}

/** Payload for PATCH /quotations/{id}: replaces all lines. */
export function toUpdateRequest(
  values: QuoteFormValues,
  version: number,
): Schemas['UpdateQuotationRequest'] {
  return { ...toBody(values), version };
}

/** Plain decimal text without trailing zeros ("12.5000" → "12.5") for editable fields. */
export function trimZeros(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '');
}

/** Form values for editing a saved quotation. */
export function fromQuotation(q: Quotation, contact: Contact | null): QuoteFormValues {
  return {
    contact,
    docDate: q.docDate,
    validUntil: q.validUntil ?? '',
    reference: q.reference ?? '',
    notes: q.notes ?? '',
    terms: q.terms ?? '',
    discountType: q.discount?.type ?? 'PERCENT',
    discountValue: q.discount ? trimZeros(q.discount.value) : '',
    lines: q.lines.map((l) => ({
      key: l.id,
      variantId: l.variantId,
      productId: null,
      sku: l.sku,
      description: l.description,
      unitId: l.unitId,
      unitSymbol: l.unitSymbol,
      factor: l.factor,
      salePrice: null,
      qty: trimZeros(l.qty),
      unitPrice: trimZeros(l.unitPrice),
      discountType: l.discount?.type ?? 'PERCENT',
      discountValue: l.discount ? trimZeros(l.discount.value) : '',
      taxRateId: l.taxRateId ?? '',
    })),
  };
}
